import React, { useMemo, useState } from 'react';
import type { ChannelDeliveryStatus, NotificationResponse } from '@troia-v3/types';

import {
  Breadcrumb,
  Button,
  FilterBottomSheet,
  FiltersButton,
  Input,
  ListPageEmpty,
  ListPageLoading,
  ListPagePagination,
  PageToolbar,
  PlainHeadCell,
  Select,
  Tooltip,
  type SelectOption,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { NOTIFICATIONS_LOG_PAGE_SIZE, useNotificationsLog } from '../hooks/useNotificationsLog';
import {
  CHANNEL_LABELS,
  NOTIFICATION_TYPE_LABELS,
  getChannelLabel,
  getNotificationTypeLabel,
} from '../utils/notificationTypeLabels';

/* ─────────────────────────── constantes ─────────────────────────── */

/** Estilo do chip de canal por status de entrega (`ChannelDeliveryStatus.status`). */
const CHANNEL_STATUS_STYLE: Record<string, { color: string; bg: string }> = {
  sent: { color: '#15803d', bg: '#f0fdf4' },
  delivered: { color: '#15803d', bg: '#f0fdf4' },
  read: { color: '#15803d', bg: '#f0fdf4' },
  failed: { color: '#b91c1c', bg: '#fef2f2' },
  pending: { color: '#64748b', bg: '#f1f5f9' },
};

const CHANNEL_STATUS_FALLBACK = { color: '#64748b', bg: '#f1f5f9' };

/** Rótulo pt-BR do status — vai no `title` do chip (junto do erro, quando houver). */
const CHANNEL_STATUS_LABEL: Record<string, string> = {
  pending: 'Pendente',
  sent: 'Enviado',
  delivered: 'Entregue',
  failed: 'Falhou',
  read: 'Lido',
};

const TYPE_OPTIONS: SelectOption[] = [
  { value: '', label: 'Todos os tipos' },
  ...Object.entries(NOTIFICATION_TYPE_LABELS)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR')),
];

const CHANNEL_OPTIONS: SelectOption[] = [
  { value: '', label: 'Todos os canais' },
  ...Object.entries(CHANNEL_LABELS).map(([value, label]) => ({ value, label })),
];

/**
 * Override de CSS variables para tema CLARO FIXO — o popover de filtros e o
 * bottom-sheet são dropdowns (regra dura do projeto), e os shared `Input`/
 * `Select`/`Button` lá dentro leem essas variáveis.
 */
const LIGHT_THEME_VARS = {
  '--theme-text': '#1e293b',
  '--theme-muted-text': '#6b7280',
  '--theme-input-background': '#ffffff',
  '--theme-input-text': '#1e293b',
  '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
  '--theme-input-focus': '#8b5cf6',
  '--theme-error-color': '#ef4444',
  '--theme-card-background': '#ffffff',
  '--theme-card-text': '#334155',
  '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-card-muted': '#94a3b8',
  '--theme-button-primary': '#8b5cf6',
  '--theme-button-primary-text': '#ffffff',
  '--theme-button-primary-hover': '#7c3aed',
  '--theme-primary': '#8b5cf6',
  '--theme-primary-background': '#f3e8ff',
  '--theme-border': 'rgba(0, 0, 0, 0.06)',
  '--theme-component-background': '#ffffff',
} as React.CSSProperties;

/* ─────────────────────────── helpers ─────────────────────────── */

/** `YYYY-MM-DD` → ISO do INÍCIO do dia local (00:00:00). */
const toStartOfDayIso = (value: string): string | undefined => {
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

/** `YYYY-MM-DD` → ISO do FIM do dia local (23:59:59.999) — mantém "Até" inclusivo. */
const toEndOfDayIso = (value: string): string | undefined => {
  const date = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
};

const formatDateTime = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('pt-BR');
};

const formatDate = (iso: string): string => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleDateString('pt-BR');
};

/** Filtros efetivamente aplicados (já em ISO, prontos pra query). */
interface LogFilters {
  dateFrom?: string;
  dateTo?: string;
  type?: string;
  channel?: string;
}

/** Rascunho do formulário do popover (datas em `YYYY-MM-DD`, como o input entrega). */
interface DraftFilters {
  dateFrom: string;
  dateTo: string;
  type: string;
  channel: string;
}

const EMPTY_DRAFT: DraftFilters = { dateFrom: '', dateTo: '', type: '', channel: '' };

/* ─────────────────────────── chips de canal ─────────────────────────── */

const ChannelChips: React.FC<{ notification: NotificationResponse }> = ({ notification }) => {
  const entries: ChannelDeliveryStatus[] = notification.deliveryStatus ?? [];

  if (entries.length === 0) {
    return (
      <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
        —
      </span>
    );
  }

  return (
    <div className="flex items-center flex-wrap gap-1.5">
      {entries.map((ds, index) => {
        const style = CHANNEL_STATUS_STYLE[ds.status] ?? CHANNEL_STATUS_FALLBACK;
        const statusLabel = CHANNEL_STATUS_LABEL[ds.status] ?? ds.status;
        const tooltipContent = ds.error ? `${statusLabel}: ${ds.error}` : statusLabel;
        const key = `${ds.channel}-${index}`;
        const chip = (
          <span
            className="inline-flex items-center h-[22px] px-2 rounded-full text-[11px] font-semibold whitespace-nowrap"
            style={{ color: style.color, backgroundColor: style.bg }}
            aria-label={`${getChannelLabel(ds.channel)}: ${statusLabel}`}
            data-testid={`notifications-log-channel-chip-${notification.id}-${ds.channel}`}
          >
            {getChannelLabel(ds.channel)}
          </span>
        );
        return tooltipContent ? (
          <Tooltip key={key} content={tooltipContent}>
            {chip}
          </Tooltip>
        ) : (
          <React.Fragment key={key}>{chip}</React.Fragment>
        );
      })}
    </div>
  );
};

ChannelChips.displayName = 'ChannelChips';

/* ─────────────────────────── página ─────────────────────────── */

/**
 * `/notifications` — Log de Notificações (READ-ONLY).
 *
 * Auditoria do que o sistema disparou para a conta do usuário, com o status de
 * entrega POR CANAL (`deliveryStatus[]`). Sem seleção em massa e sem ações por
 * linha: é log, não caixa de entrada (o sino continua sendo a caixa).
 */
export const NotificationsLogPage: React.FC = () => {
  const [filters, setFilters] = useState<LogFilters>({});
  const [draft, setDraft] = useState<DraftFilters>(EMPTY_DRAFT);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useNotificationsLog({ page, ...filters });

  const items = useMemo(() => data?.notifications ?? [], [data]);
  const total = data?.total ?? 0;
  const pageSize = data?.limit ?? NOTIFICATIONS_LOG_PAGE_SIZE;
  const totalPages = Math.max(1, Math.ceil(total / (pageSize || NOTIFICATIONS_LOG_PAGE_SIZE)));

  const activeCount = Object.values(filters).filter(Boolean).length;

  /** Aplica o rascunho: converte datas para ISO e volta pra página 1. */
  const applyFilters = () => {
    const next: LogFilters = {};
    if (draft.dateFrom) {
      const iso = toStartOfDayIso(draft.dateFrom);
      if (iso) next.dateFrom = iso;
    }
    if (draft.dateTo) {
      const iso = toEndOfDayIso(draft.dateTo);
      if (iso) next.dateTo = iso;
    }
    if (draft.type) next.type = draft.type;
    if (draft.channel) next.channel = draft.channel;

    setFilters(next);
    setPage(1);
    setFiltersOpen(false);
  };

  /** Remove um filtro pelo chip — some do rascunho também (o popover reabre coerente). */
  const removeFilter = (key: keyof LogFilters) => {
    setFilters((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setDraft((prev) => ({ ...prev, [key]: '' }));
    setPage(1);
  };

  const clearAllFilters = () => {
    setFilters({});
    setDraft(EMPTY_DRAFT);
    setPage(1);
    setFiltersOpen(false);
  };

  const chips: Array<{ key: keyof LogFilters; label: string }> = [];
  if (filters.dateFrom) chips.push({ key: 'dateFrom', label: `De: ${formatDate(filters.dateFrom)}` });
  if (filters.dateTo) chips.push({ key: 'dateTo', label: `Até: ${formatDate(filters.dateTo)}` });
  if (filters.type) chips.push({ key: 'type', label: `Tipo: ${getNotificationTypeLabel(filters.type)}` });
  if (filters.channel) chips.push({ key: 'channel', label: `Canal: ${getChannelLabel(filters.channel)}` });

  /**
   * Conteúdo dos filtros — o MESMO formulário serve o popover (desktop) e o
   * bottom-sheet (mobile), que coexistem no DOM (um escondido por CSS). O
   * `suffix` mantém `id`/`data-testid` únicos entre as duas cópias (padrão
   * `testIdSuffix` do DS — ver QualityReportsPage).
   */
  const renderFilterForm = (suffix: string) => (
    <div className="p-2.5 space-y-3" style={LIGHT_THEME_VARS}>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label
            className="block text-[11px] font-bold uppercase tracking-wider mb-1.5"
            style={{ color: '#94a3b8' }}
            htmlFor={`notifications-log-date-from${suffix}`}
          >
            De
          </label>
          <Input
            id={`notifications-log-date-from${suffix}`}
            type="date"
            value={draft.dateFrom}
            onChange={(e) => setDraft((prev) => ({ ...prev, dateFrom: e.target.value }))}
            data-testid={`notifications-log-date-from${suffix}`}
          />
        </div>
        <div>
          <label
            className="block text-[11px] font-bold uppercase tracking-wider mb-1.5"
            style={{ color: '#94a3b8' }}
            htmlFor={`notifications-log-date-to${suffix}`}
          >
            Até
          </label>
          <Input
            id={`notifications-log-date-to${suffix}`}
            type="date"
            value={draft.dateTo}
            onChange={(e) => setDraft((prev) => ({ ...prev, dateTo: e.target.value }))}
            data-testid={`notifications-log-date-to${suffix}`}
          />
        </div>
      </div>

      <Select
        id={`notifications-log-type${suffix}`}
        label="Tipo"
        options={TYPE_OPTIONS}
        value={draft.type}
        onChange={(value) => setDraft((prev) => ({ ...prev, type: value }))}
        data-testid={`notifications-log-type${suffix}`}
      />

      <Select
        id={`notifications-log-channel${suffix}`}
        label="Canal"
        options={CHANNEL_OPTIONS}
        value={draft.channel}
        onChange={(value) => setDraft((prev) => ({ ...prev, channel: value }))}
        data-testid={`notifications-log-channel${suffix}`}
      />

      <div className="flex items-center justify-end gap-2 pt-1">
        <Button variant="ghost" onClick={clearAllFilters} data-testid={`notifications-log-reset${suffix}`}>
          Limpar
        </Button>
        <Button variant="primary" onClick={applyFilters} data-testid={`notifications-log-apply${suffix}`}>
          Aplicar
        </Button>
      </div>
    </div>
  );

  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
      data-testid="notifications-log-page"
    >
      {/* ── Toolbar (contrato DS 3.3 — breadcrumb, sem seta voltar) ── */}
      <PageToolbar
        data-testid="notifications-log-toolbar"
        left={
          <>
            <Breadcrumb
              items={[{ label: 'Notificações', testid: 'notifications-log-breadcrumb-current' }]}
              data-testid="notifications-log-breadcrumb"
            />
            {data?.total != null && (
              <span
                className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
                style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
                data-testid="notifications-log-count"
              >
                {data.total}
              </span>
            )}
          </>
        }
        right={
          <div className="relative flex-none">
            <FiltersButton
              count={activeCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="notifications-log-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="hidden sm:block absolute right-0 top-full mt-2 z-50 w-[320px] rounded-xl max-h-[70vh] overflow-y-auto"
                  style={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    boxShadow: '0 4px 24px rgba(0,0,0,.12)',
                  }}
                  data-testid="notifications-log-filters-popover"
                >
                  {renderFilterForm('')}
                </div>
              </>
            )}
          </div>
        }
        mobile={
          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
            style={{
              border: '1px solid var(--theme-input-border)',
              backgroundColor: 'var(--theme-card-background)',
              color: 'var(--theme-card-text)',
            }}
            data-testid="notifications-log-filters-mobile"
          >
            <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
            Filtros
            {activeCount > 0 && (
              <span
                className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                {activeCount}
              </span>
            )}
          </button>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {chips.length > 0 && (
        <div
          className="flex-none flex items-center flex-wrap gap-2 px-4 py-2"
          style={{
            backgroundColor: 'var(--theme-header-background)',
            borderBottom: '1px solid var(--theme-header-border)',
          }}
          data-testid="notifications-log-filterbar"
        >
          {chips.map((chip) => (
            <span
              key={chip.key}
              className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
            >
              {chip.label}
              <button
                type="button"
                onClick={() => removeFilter(chip.key)}
                className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100"
                aria-label={`Remover filtro ${chip.label}`}
                data-testid={`notifications-log-chip-remove-${chip.key}`}
              >
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={clearAllFilters}
            className="text-xs font-semibold"
            style={{ color: 'var(--theme-muted-text)' }}
            data-testid="notifications-log-clear-filters"
          >
            Limpar tudo
          </button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-8">
          <p className="text-sm mb-4" style={{ color: 'var(--theme-muted-text)' }}>
            Log de envio por canal — auditoria das notificações da sua conta.
          </p>

          {error && (
            <div
              className="rounded-lg p-4 mb-4"
              style={{
                backgroundColor: 'var(--theme-error-subtle)',
                border: '1px solid var(--theme-error-color)',
              }}
              data-testid="notifications-log-error"
            >
              <p style={{ color: 'var(--theme-error-fg)' }}>
                Erro ao carregar notificações: {error.message}
              </p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando notificações..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="bell"
              message="Nenhuma notificação no período."
              description={
                activeCount > 0
                  ? 'Tente ajustar os filtros de data, tipo ou canal.'
                  : 'Assim que o sistema enviar notificações para você, elas aparecem aqui.'
              }
            />
          )}

          {!isLoading && !error && items.length > 0 && (
            <>
              {/* Mobile: cards empilhados */}
              <div className="grid grid-cols-1 gap-3 md:hidden">
                {items.map((notification) => (
                  <div
                    key={notification.id}
                    className="rounded-xl p-4"
                    style={{
                      backgroundColor: 'var(--theme-card-background)',
                      border: '1px solid var(--theme-card-border)',
                    }}
                    data-testid={`notifications-log-card-${notification.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="text-xs font-semibold" style={{ color: 'var(--theme-primary)' }}>
                        {getNotificationTypeLabel(notification.type)}
                      </span>
                      <span
                        className="text-xs tabular-nums flex-none"
                        style={{ color: 'var(--theme-muted-text)' }}
                      >
                        {formatDateTime(notification.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                      {notification.title}
                    </p>
                    <p className="text-[13px] mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
                      {notification.message}
                    </p>
                    <div className="mt-2.5">
                      <ChannelChips notification={notification} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: tabela */}
              <div
                className="hidden md:block rounded-xl overflow-hidden"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  border: '1px solid var(--theme-card-border)',
                }}
              >
                <table className="w-full border-collapse" data-testid="notifications-log-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <PlainHeadCell label="Data/hora" className="w-48" />
                      <PlainHeadCell label="Tipo" className="w-56" />
                      <PlainHeadCell label="Notificação" />
                      <PlainHeadCell label="Canais" className="w-72" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((notification, index) => (
                      <tr
                        key={notification.id}
                        className={index > 0 ? 'border-t' : ''}
                        style={index > 0 ? { borderColor: 'var(--theme-card-border)' } : {}}
                        data-testid={`notifications-log-row-${notification.id}`}
                      >
                        <td
                          className="px-4 py-3 text-[13px] tabular-nums whitespace-nowrap align-top"
                          style={{ color: 'var(--theme-muted-text)' }}
                        >
                          {formatDateTime(notification.createdAt)}
                        </td>
                        <td
                          className="px-4 py-3 text-[13px] align-top"
                          style={{ color: 'var(--theme-card-text)' }}
                        >
                          {getNotificationTypeLabel(notification.type)}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <p className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                            {notification.title}
                          </p>
                          {notification.message ? (
                            <Tooltip content={notification.message}>
                              <p
                                className="text-[13px] truncate max-w-md"
                                style={{ color: 'var(--theme-muted-text)' }}
                              >
                                {notification.message}
                              </p>
                            </Tooltip>
                          ) : (
                            <p
                              className="text-[13px] truncate max-w-md"
                              style={{ color: 'var(--theme-muted-text)' }}
                            >
                              {notification.message}
                            </p>
                          )}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <ChannelChips notification={notification} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {!isLoading && !error && (
            <ListPagePagination
              currentPage={page}
              totalPages={totalPages}
              totalItems={total}
              pageSize={pageSize}
              itemLabel="notificações"
              onPageChange={setPage}
            />
          )}

          <p className="text-xs mt-6" style={{ color: 'var(--theme-muted-text)', opacity: 0.85 }}>
            O log mostra notificações criadas pelo sistema. Push para usuário sem dispositivo
            registrado não gera registro.
          </p>
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — mesmo conteúdo do popover */}
      <FilterBottomSheet
        isOpen={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        data-testid="notifications-log-filters-sheet"
      >
        {renderFilterForm('-m')}
      </FilterBottomSheet>
    </div>
  );
};

NotificationsLogPage.displayName = 'NotificationsLogPage';
